import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { userIsNiStaff } from '../../../roles';
import { ACCENT, QPF_AXES, num } from './supplierUi';
import { CapabilityEditor, newCapability, capabilityPayload, capabilityHasContent } from './SupplierCapabilityFields';

const API_BASE = process.env.REACT_APP_API_URL;
const SUPPLIER_KEYS = ['', 'gpv', 'ubiqconn', 'acmepoint', 'hk_ringer'];

const inp = { background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)', borderRadius: 6, padding: '8px 12px', fontSize: 13, width: '100%', boxSizing: 'border-box' };
const label = { display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ni-text-muted)', marginBottom: 5 };
const sectionTitle = { fontSize: 13, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text)', margin: '10px 0 0' };

export default function SupplierNew() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();

  // Same staff gate as SuppliersLibrary/SupplierDetail — the shared helper
  // handles role arrays, all metadata sources and service* variants.
  const canManage = userIsNiStaff({ publicMetadata: user?.publicMetadata });

  const [docNum, setDocNum] = useState('');
  const [types, setTypes] = useState([]);
  const [standings, setStandings] = useState([]);
  const [capCats, setCapCats] = useState([]);
  const [recUses, setRecUses] = useState([]);
  const [riskLevels, setRiskLevels] = useState([]);
  const [form, setForm] = useState({
    name: '', legalName: '', supplierType: 'Other', standing: 'Active',
    website: '', country: '', region: '', location: '', overview: '', supplierKey: '',
    leadTimeDays: '', riskLevel: '', singleSource: false, tags: '',
    qpf: { quality: null, price: null, speed: null },
    capabilities: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!canManage) { navigate('/quality/suppliers'); return; }
    getToken().then(token => {
      window.fetch(`${API_BASE}/quality/next-doc-number?prefix=SUP-NIN-`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(d => { if (d.ok) setDocNum(d.documentNumber); });
      window.fetch(`${API_BASE}/quality/suppliers/meta/types`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(d => {
          if (d.ok) {
            setTypes(d.types || []); setStandings(d.standings || []);
            setCapCats(d.capabilityCategories || []); setRecUses(d.recommendedUses || []); setRiskLevels(d.riskLevels || []);
          }
        });
    });
  }, [canManage, navigate, getToken]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setQpf = (k, v) => setForm(f => ({ ...f, qpf: { ...f.qpf, [k]: v } }));
  const setCap = (i, k, v) => setForm(f => ({ ...f, capabilities: f.capabilities.map((c, j) => j === i ? { ...c, [k]: v } : c) }));
  const addCap = () => setForm(f => ({ ...f, capabilities: [...f.capabilities, newCapability()] }));
  const removeCap = (i) => setForm(f => ({ ...f, capabilities: f.capabilities.filter((_, j) => j !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    // A row with a rating/use/notes but no category would otherwise be lost silently.
    const incomplete = form.capabilities.some(c =>
      !(c.category || '').trim() && capabilityHasContent(c));
    if (incomplete) { setError('Every capability row needs a category — pick one or remove the row.'); return; }
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const body = {
        ...form,
        documentNumber: docNum,
        leadTimeDays: num(form.leadTimeDays),
        tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
        capabilities: form.capabilities.filter(c => (c.category || '').trim()).map(capabilityPayload),
      };
      const res = await window.fetch(`${API_BASE}/quality/suppliers`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error((data.errors || []).join(' · ') || data.error || 'Failed to create supplier');
      navigate(`/quality/suppliers/${data.supplier._id}`);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  if (!canManage) return null;

  return (
    <div style={{ maxWidth: 720 }}>
      <button onClick={() => navigate('/quality/suppliers')}
        style={{ background: 'none', border: 'none', color: 'var(--ni-text-muted)', cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 12 }}>
        ← Back to suppliers
      </button>
      <h2 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800 }}>New Supplier</h2>
      <p style={{ margin: '0 0 22px', color: 'var(--ni-text-muted)', fontSize: 13 }}>
        Capture the comparable facts now — capabilities, quick rating and risk — so the new supplier shows up
        properly in the Grid and Map from day one. Contacts, terms and the scorecard live on the supplier page.
      </p>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={label}>Document Number</label>
              <input value={docNum || 'Generating…'} onChange={e => setDocNum(e.target.value)} style={{ ...inp, fontFamily: 'monospace' }} placeholder="SUP-NIN-001" />
            </div>
            <div>
              <label style={label}>Type</label>
              <select value={form.supplierType} onChange={e => set('supplierType', e.target.value)} style={inp}>
                {types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label style={label}>Name</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} style={inp} placeholder="e.g. GPV" required />
          </div>
          <div>
            <label style={label}>Legal Name</label>
            <input value={form.legalName} onChange={e => set('legalName', e.target.value)} style={inp} placeholder="Full registered name (optional)" />
          </div>
          <div>
            <label style={label}>Tripletex supplier number</label>
            <input value={form.tripletexSupplierNumber || ''} onChange={e => set('tripletexSupplierNumber', e.target.value)} style={inp} placeholder="Reference only; no automatic accounting update" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={label}>Standing</label>
              <select value={form.standing} onChange={e => set('standing', e.target.value)} style={inp}>
                {standings.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={label}>Portal supplier key</label>
              <select value={form.supplierKey} onChange={e => set('supplierKey', e.target.value)} style={inp}>
                {SUPPLIER_KEYS.map(k => <option key={k} value={k}>{k || '— none —'}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
            <div>
              <label style={label}>Country</label>
              <input value={form.country} onChange={e => set('country', e.target.value)} style={inp} placeholder="e.g. Denmark" />
            </div>
            <div>
              <label style={label}>Region</label>
              <input value={form.region} onChange={e => set('region', e.target.value)} style={inp} placeholder="e.g. Asia, Nordics" />
            </div>
            <div>
              <label style={label}>Location</label>
              <input value={form.location} onChange={e => set('location', e.target.value)} style={inp} placeholder="City / site" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
            <div>
              <label style={label}>Lead time (days)</label>
              <input type="number" value={form.leadTimeDays} onChange={e => set('leadTimeDays', e.target.value)} style={inp} placeholder="typical quote" />
            </div>
            <div>
              <label style={label}>Risk level</label>
              <select value={form.riskLevel} onChange={e => set('riskLevel', e.target.value)} style={inp}>
                {riskLevels.map(r => <option key={r} value={r}>{r || '—'}</option>)}
              </select>
            </div>
            <div>
              <label style={label}>Single source</label>
              <select value={form.singleSource ? 'yes' : 'no'} onChange={e => set('singleSource', e.target.value === 'yes')} style={inp}>
                <option value="no">No</option><option value="yes">Yes</option>
              </select>
            </div>
          </div>

          <div style={sectionTitle}>Quick rating</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
            {QPF_AXES.map(a => (
              <div key={a.key}>
                <label style={{ ...label, color: a.color }}>{a.label} (1–5)</label>
                <select value={form.qpf[a.key] ?? ''} onChange={e => setQpf(a.key, num(e.target.value))} style={inp}>
                  <option value="">—</option>
                  {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={sectionTitle}>Capabilities</div>
            <button type="button" onClick={addCap}
              style={{ background: 'transparent', color: ACCENT, border: `1px solid ${ACCENT}66`, borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              + Add capability
            </button>
          </div>
          {form.capabilities.length === 0 && (
            <div style={{ color: 'var(--ni-text-muted)', fontSize: 13, marginTop: -8 }}>
              What can they do for us? Each line becomes a column in the Grid and a node in the Map.
            </div>
          )}
          {form.capabilities.map((cap, i) => <CapabilityEditor key={i} cap={cap} categories={capCats} recommendedUses={recUses} creating onChange={(k, v) => setCap(i, k, v)} onRemove={() => removeCap(i)} />)}

          <div>
            <label style={label}>Tags</label>
            <input value={form.tags} onChange={e => set('tags', e.target.value)} style={inp} placeholder="comma separated, e.g. mechanical, hardware" />
          </div>
          <div>
            <label style={label}>Website</label>
            <input value={form.website} onChange={e => set('website', e.target.value)} style={inp} placeholder="https://…" />
          </div>
          <div>
            <label style={label}>Overview (what they do for us)</label>
            <textarea value={form.overview} onChange={e => set('overview', e.target.value)} rows={3} style={{ ...inp, resize: 'vertical' }} placeholder="Capabilities summary…" />
          </div>

          {error && (
            <div style={{ color: '#EC2124', fontSize: 13, padding: '8px 12px', background: 'rgba(236,33,36,0.1)', borderRadius: 6 }}>{error}</div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" disabled={saving}
              style={{ background: ACCENT, color: '#000', border: 'none', borderRadius: 6, padding: '10px 24px', fontWeight: 700, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Creating…' : 'Create Supplier'}
            </button>
            <button type="button" onClick={() => navigate('/quality/suppliers')}
              style={{ background: 'transparent', color: 'var(--ni-text-muted)', border: '1px solid var(--ni-border-soft)', borderRadius: 6, padding: '10px 20px', fontSize: 14, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
